import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Icons } from "../../constants/icon-library";
import LoadingComponent from "../../components/common/LoadingComponent";
import {
  AVATAR_COLORS,
  CATEGORY_BADGE_CLASS,
  CATEGORY_CONFIG,
  STATUS_BADGE_CLASS,
  formatDurationLabel,
  formatRelativeTime,
} from "./Dashboard.utils";
import { getDashboardSummary } from "../../features/tasks/taskSlice";
import CircularProgressMeter from "./CircularProgressMeter";

function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { dashboardSummary, dashboardLoading } = useSelector(
    (state) => state.task,
  );

  const {
    categoryCounts = { lead: 0, complaint: 0, query: 0, noise: 0 },
    recentActions = [],
    operationalHealthMetrics,
    teamPerformanceStats = [],
    closedOutcomeBreakdown = { outcomesList: [], totalActionsCount: 0 },
  } = dashboardSummary || {};

  useEffect(() => {
    dispatch(getDashboardSummary());
  }, [dispatch]);

  if (dashboardLoading && !operationalHealthMetrics) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <LoadingComponent loading={true} size={15} />
      </div>
    );
  }

  if (!operationalHealthMetrics) {
    return null;
  }

  return (
    <div
      className="content"
      style={{ maxWidth: "100%", padding: "4px 20px", marginTop: "40px" }}
    >
      <div className="row g-3 mb-4">
        <div className="col-12 col-lg-8">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <h1 className="display-5 fw-bold text-danger mb-2">
                {operationalHealthMetrics.atRiskCount}
              </h1>

              <p className="text-muted mb-3">
                <strong>{operationalHealthMetrics.overdueCount}</strong> task
                {operationalHealthMetrics.overdueCount === 1 ? "" : "s"} have
                gone more than 24 hours with no update.
                {operationalHealthMetrics.agingComplaintsCount > 0 && (
                  <>
                    {" "}
                    Plus{" "}
                    <strong>
                      {operationalHealthMetrics.agingComplaintsCount}
                    </strong>{" "}
                    complaint
                    {operationalHealthMetrics.agingComplaintsCount === 1
                      ? ""
                      : "s"}{" "}
                    aging publicly
                  </>
                )}
                {operationalHealthMetrics.unansweredQueriesCount > 0 && (
                  <>
                    {" "}
                    and{" "}
                    <strong>
                      {operationalHealthMetrics.unansweredQueriesCount}
                    </strong>{" "}
                    customer quer
                    {operationalHealthMetrics.unansweredQueriesCount === 1
                      ? "y"
                      : "ies"}{" "}
                    unanswered.
                  </>
                )}
              </p>

              <hr />

              <div className="d-flex gap-4 flex-wrap">
                <div>
                  <h5 className="mb-0 text-danger">
                    {operationalHealthMetrics.openActionsTotal}
                  </h5>
                  <span className="text-muted small">Open total</span>
                </div>
                <div>
                  <h5 className="mb-0 text-warning">
                    {operationalHealthMetrics.unassignedActionsCount}
                  </h5>
                  <span className="text-muted small">Unassigned</span>
                </div>
                <div>
                  <h5 className="mb-0 text-secondary">
                    {operationalHealthMetrics.failedActionsCount}
                  </h5>
                  <span className="text-muted small">Failed, unresolved</span>
                </div>
                <div>
                  <h5 className="mb-0 text-success">
                    {operationalHealthMetrics.completedThisWeekCount}
                  </h5>
                  <span className="text-muted small">Completed this week</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4 d-flex flex-column gap-3">
          <div className="card border shadow-sm flex-fill">
            <div className="card-body d-flex align-items-center gap-3">
              <CircularProgressMeter
                percent={operationalHealthMetrics.slaCompliancePercent}
                centerLabel={`${operationalHealthMetrics.slaCompliancePercent}%`}
                color={
                  operationalHealthMetrics.slaCompliancePercent >= 80
                    ? "success"
                    : operationalHealthMetrics.slaCompliancePercent >= 50
                      ? "warning"
                      : "danger"
                }
              />
              <div>
                <h6 className="fw-semibold mb-1">Response SLA</h6>
                <div className="small text-muted">
                  Only {operationalHealthMetrics.slaCompliancePercent}% resolved
                  in time
                </div>
                <div className="small text-muted">
                  Across leads, complaints &amp; queries
                </div>
              </div>
            </div>
          </div>

          <div className="card border shadow-sm flex-fill">
            <div className="card-body d-flex align-items-center gap-3">
              <CircularProgressMeter
                percent={operationalHealthMetrics.complaintsAgingPercent}
                centerLabel={operationalHealthMetrics.agingComplaintsCount}
                color="warning"
              />
              <div>
                <h6 className="fw-semibold mb-1">Public complaints aging</h6>
                <div className="small text-muted">
                  Visible on your ads/posts
                </div>
                <div className="small text-muted">
                  Potential buyers can see them
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {CATEGORY_CONFIG.map((cat) => (
          <div className="col-6 col-md-3" key={cat.key}>
            <div
              className="card h-100 shadow-sm border"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/tasks")}
            >
              <div className="card-body d-flex align-items-center gap-3">
                <span
                  className={`d-inline-flex align-items-center justify-content-center rounded-circle bg-${cat.color}-subtle flex-shrink-0`}
                  style={{ width: 24, height: 24 }}
                >
                  <i
                    className={`wb ${Icons("dot")} text-${cat.color}`}
                    style={{ fontSize: "10px" }}
                  />
                </span>
                <div>
                  <h4 className="mb-0">{categoryCounts[cat.key]}</h4>
                  <span className="text-muted small">{cat.label}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-body-emphasis rounded-3 p-4 border mb-4">
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
          <h3 className="mb-0 fw-semibold d-flex align-items-center gap-2 text-body">
            <i className={`wb ${Icons("clock")} fs-8`} />
            Recent Tasks
          </h3>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/tasks")}
          >
            View all{" "}
            <i className={`wb ${Icons("redirect")} ms-1 align-middle`} />
          </button>
        </div>

        {recentActions.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-inbox fs-1 d-block mb-2" />
            <p className="mb-0">No tasks found across any integrations.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr
                  className="text-uppercase text-muted"
                  style={{ fontSize: "11px" }}
                >
                  <th className="ps-3">Task</th>
                  <th>Type</th>
                  <th>Created</th>
                  <th className="pe-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentActions.map((action) => (
                  <tr
                    key={action.uuid}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/tasks")}
                  >
                    <td className="ps-3">
                      <div
                        className="text-truncate fw-semibold"
                        style={{ maxWidth: 360 }}
                        title={action.name}
                      >
                        {action.name}
                      </div>
                      <div
                        className="text-muted small text-truncate"
                        style={{ maxWidth: 360 }}
                      >
                        {action.description}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge text-uppercase ${
                          CATEGORY_BADGE_CLASS[action.category] ||
                          "bg-secondary-subtle text-secondary"
                        }`}
                        style={{ fontSize: "10px" }}
                      >
                        {action.category || "noise"}
                      </span>
                    </td>
                    <td className="small text-muted">
                      {formatRelativeTime(action.createdAt)}
                    </td>
                    <td className="pe-3">
                      <span
                        className={`badge text-capitalize ${
                          STATUS_BADGE_CLASS[action.status] ||
                          "bg-secondary-subtle text-secondary"
                        }`}
                        style={{ fontSize: "10px" }}
                      >
                        {action.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="row g-3">
        <div className="col-12 col-lg-6">
          <div className="bg-body-emphasis rounded-3 p-4 border h-100">
            <h5 className="fw-semibold mb-1">Team this week</h5>
            <p className="text-muted small mb-4">
              Who's handling what — and who's dropping things.
            </p>

            {teamPerformanceStats.length === 0 ? (
              <div className="text-center py-4 text-muted small">
                No assigned tasks in the last 7 days.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr
                      className="text-uppercase text-muted"
                      style={{ fontSize: "10px" }}
                    >
                      <th>Rep</th>
                      <th>Avg close time</th>
                      <th>Handled</th>
                      <th>Dropped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamPerformanceStats.map((rep, idx) => (
                      <tr key={rep.uuid}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className={`d-flex align-items-center justify-content-center rounded-circle text-white fw-semibold bg-${
                                AVATAR_COLORS[idx % AVATAR_COLORS.length]
                              }`}
                              style={{ width: 28, height: 28, fontSize: 12 }}
                            >
                              {rep.name.charAt(0).toUpperCase()}
                            </span>
                            <span>{rep.name}</span>
                          </div>
                        </td>
                        <td
                          className={
                            rep.avgCloseTimeMs &&
                            rep.avgCloseTimeMs < 15 * 60000
                              ? "text-success"
                              : rep.avgCloseTimeMs &&
                                  rep.avgCloseTimeMs > 30 * 60000
                                ? "text-warning"
                                : ""
                          }
                        >
                          {formatDurationLabel(rep.avgCloseTimeMs)}
                        </td>
                        <td>
                          {rep.handledCount}{" "}
                          {rep.categoryBreakdownLabel && (
                            <span className="text-muted small">
                              ({rep.categoryBreakdownLabel})
                            </span>
                          )}
                        </td>
                        <td
                          className={
                            rep.droppedCount > 0
                              ? "text-danger"
                              : "text-success"
                          }
                        >
                          {rep.droppedCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="bg-body-emphasis rounded-3 p-4 border h-100">
            <h5 className="fw-semibold mb-1">How things closed</h5>
            <p className="text-muted small mb-4">
              All types, this week — by outcome.
            </p>

            {closedOutcomeBreakdown.outcomesList.length === 0 ? (
              <div className="text-center py-4 text-muted small">
                No completed or failed tasks in the last 7 days.
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {closedOutcomeBreakdown.outcomesList.map((outcome) => (
                  <div key={outcome.label}>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="d-flex align-items-center gap-2 small">
                        <span
                          className={`rounded-circle bg-${outcome.color}`}
                          style={{
                            width: 8,
                            height: 8,
                            display: "inline-block",
                          }}
                        />
                        {outcome.label}
                      </span>
                      <span className="small text-muted">
                        {outcome.count} of{" "}
                        {closedOutcomeBreakdown.totalActionsCount} (
                        {outcome.percentOfTotal}%)
                      </span>
                    </div>
                    <div className="progress" style={{ height: 6 }}>
                      <div
                        className={`progress-bar bg-${outcome.color}`}
                        style={{ width: `${outcome.percentOfTotal}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
